import { createContext, useContext, useEffect } from 'react';
import type {
  AssistantAction,
  AssistantAttachmentInput,
  AssistantChoice,
  AssistantDraft,
  AssistantIdKind,
  AssistantQuestion,
  AssistantView,
} from '../../types/assistant';

/** One message in the thread. The views and drafts it brought are referenced by key. */
export interface AssistantMessage {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  /** A failed turn's note: shown, never sent back as history. */
  failed?: boolean;
  /** A note the app added (a saved draft), not the assistant's words: shown, never sent back. */
  local?: boolean;
  viewKeys: string[];
  draftKeys: string[];
  suggestions: string[];
  question: AssistantQuestion | null;
  /** A note's link: where a saved draft opens (a same-site path). */
  openPath?: string | null;
  /** The link's words ("Open board"); "Open it" when absent. */
  openLabel?: string | null;
  /** The names of the files a coach's message carried, shown under it. Local display only: never sent back. */
  attachments?: string[];
}

/**
 * How a send ended. `refused`: the server turned it away before it started (a file it would not take, a rate limit),
 * so the composer keeps the files for the coach to fix. `stopped`: Stop, or a newer message. `ignored`: nothing sent.
 */
export type AssistantSendOutcome = 'replied' | 'failed' | 'refused' | 'stopped' | 'ignored';

/** A file the coach attached, read and ready to send: its name, size (bytes) and data URL. */
export interface AssistantAttachment extends AssistantAttachmentInput {
  size: number;
}

/** A view the assistant brought, keyed across turns (a view's own id is only unique in its turn). */
export interface ShownView {
  key: string;
  view: AssistantView;
}

/** A draft on screen, as the coach has edited it so far. */
export interface OpenDraft {
  key: string;
  draft: AssistantDraft;
  saving: boolean;
  error: string | null;
}

export type AssistantPrimary = { type: 'view'; key: string } | { type: 'draft'; key: string } | null;

export type ScreenIds = Partial<Record<AssistantIdKind, string[]>>;

export interface AssistantContextValue {
  /** True when the `ai.assistant` flag is on for this user. Everything else is inert while false. */
  enabled: boolean;
  isOpen: boolean;
  /** False when the last open asked not to move focus into the panel (`open({ focus: false })`). */
  openFocus: boolean;
  open: (options?: { focus?: boolean }) => void;
  close: () => void;
  toggle: () => void;
  messages: AssistantMessage[];
  views: ShownView[];
  drafts: OpenDraft[];
  /** What the panel's view column shows. Null: chat only. */
  primary: AssistantPrimary;
  showView: (key: string) => void;
  showDraft: (key: string) => void;
  /** Puts the view column away (the next answer that shows something brings it back). */
  hidePrimary: () => void;
  /** What the assistant is doing ("Searching D1 shortstops"), null when idle. */
  status: string | null;
  busy: boolean;
  /**
   * Sends a message. `choice` is a tapped answer: its target rides as screen context. `attachments` are this message's
   * files, sent with this turn only. With files the text may be empty.
   */
  send: (text: string, choice?: AssistantChoice, attachments?: readonly AssistantAttachment[]) => Promise<AssistantSendOutcome>;
  stop: () => void;
  /** Starts a new conversation (drafts on screen included). */
  reset: () => void;
  runAction: (action: AssistantAction, item?: unknown) => void;
  /** Opens a draft the app made (Text from a player row, say) as if the assistant drafted it. */
  openDraft: (draft: AssistantDraft) => void;
  updateDraft: (key: string, payload: unknown) => void;
  discardDraft: (key: string) => void;
  confirmDraft: (key: string) => void;
  /** A page says which real ids it shows, so "these players" means them. Cleared when it unmounts. */
  setScreenIds: (owner: string, ids: ScreenIds | null) => void;
  /** The last notice for the live region (a saved draft, an action's message). */
  notice: string | null;
}

const inert: AssistantContextValue = {
  enabled: false,
  isOpen: false,
  openFocus: true,
  open: () => undefined,
  close: () => undefined,
  toggle: () => undefined,
  messages: [],
  views: [],
  drafts: [],
  primary: null,
  showView: () => undefined,
  showDraft: () => undefined,
  hidePrimary: () => undefined,
  status: null,
  busy: false,
  send: () => Promise.resolve('ignored'),
  stop: () => undefined,
  reset: () => undefined,
  runAction: () => undefined,
  openDraft: () => undefined,
  updateDraft: () => undefined,
  discardDraft: () => undefined,
  confirmDraft: () => undefined,
  setScreenIds: () => undefined,
  notice: null,
};

/** Outside an `AssistantProvider` the assistant is off: every call does nothing. */
export const AssistantContext = createContext<AssistantContextValue>(inert);

export const useAssistant = (): AssistantContextValue => useContext(AssistantContext);

/**
 * Tells the assistant which real ids this page shows (`{ player: [...] }`), so a follow-up like
 * "add these to a board" means them. Pass a stable `owner` per page; null or an empty object clears.
 */
export const useAssistantScreenIds = (owner: string, ids: ScreenIds | null): void => {
  const { setScreenIds, enabled } = useAssistant();
  const key = ids ? JSON.stringify(ids) : '';
  useEffect(() => {
    if (!enabled) return;
    setScreenIds(owner, key ? (JSON.parse(key) as ScreenIds) : null);
    return () => setScreenIds(owner, null);
  }, [enabled, owner, key, setScreenIds]);
};
